import * as React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import moment from 'moment'
import { Button, Checkbox } from '@/ui'
import { Popup } from '@/ui/overlays/Popup'
import { TextAreaField } from '@/ui/form/controls/TextAreaField'
import { FaIcon, faNoteSolid } from '@/lib/fa'
import { toast } from '@/lib/toasts/api'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { usePermissionChecker } from '@/modules/shared/hooks/usePermissionChecker'
import Tooltip from '@/modules/drivers/shared/ToolTip'
import {
  DAY_NOTES_MAX_LENGTH,
  DriverDayInfo,
  saveDriverDayInfo,
} from '@/lib/api/company/driverDay/api'
import {
  driverDayInfoKey,
  useQueryDriverDayInfo,
} from '@/lib/api/company/driverDay/useQueryDriverDayInfo'

const DayInfoButton = ({
  companyId,
  driverId,
  date,
  onViolationsChanged,
}: {
  companyId: string
  driverId: number
  /** YYYY-MM-DD */
  date: string
  onViolationsChanged: () => void
}) => {
  const { t } = useLocalize()
  const can = usePermissionChecker(companyId)
  const queryClient = useQueryClient()
  const day = { companyId, driverId, date }
  const { data: saved } = useQueryDriverDayInfo(day)
  const [draft, setDraft] = React.useState<DriverDayInfo | null>(null)
  const [saving, setSaving] = React.useState(false)

  React.useEffect(() => {
    setDraft(null)
  }, [companyId, driverId, date])

  const close = () => {
    if (!saving) setDraft(null)
  }

  const save = async () => {
    if (!draft || !saved) return
    setSaving(true)
    try {
      const { data } = await saveDriverDayInfo({
        ...day,
        notes: draft.notes?.trim() || null,
        switchViolation: draft.switchViolation,
      })
      const violationsChanged = data.switchViolation !== saved.switchViolation
      queryClient.setQueryData(driverDayInfoKey(day), data)
      setDraft(null)
      if (violationsChanged) onViolationsChanged()
    } catch (error) {
      console.error('DayInfoButton: save failed', error)
      toast.error({ text: t('driverModule.day-info-save-error') })
    } finally {
      setSaving(false)
    }
  }

  const canEdit = can('canEditActivityDay')
  const checkboxId = `day-switch-violation-${driverId}-${date}`

  return (
    <>
      <Tooltip
        portal
        text={t('driverModule.day-info-title')}
        disabled={!saved}
        showDot={Boolean(saved?.notes || saved?.switchViolation)}
        onClick={() => saved && setDraft(saved)}
      >
        <FaIcon icon={faNoteSolid} className="tw-text-primary" />
      </Tooltip>
      {draft && (
        <Popup open onClose={close}>
          <div className="tw-flex tw-w-[520px] tw-max-w-full tw-flex-col tw-gap-4 tw-p-6">
            <h2 className="tw-text-lg tw-font-semibold">
              {t('driverModule.day-info-title')}{' '}
              {moment(date).format('DD.MM.YYYY')}
            </h2>
            <TextAreaField
              fullWidth
              autoFocus
              rows={4}
              maxLength={DAY_NOTES_MAX_LENGTH}
              label={t('driverModule.day-info')}
              value={draft.notes ?? ''}
              disabled={!canEdit || saving}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            />
            <label
              htmlFor={checkboxId}
              className="tw-m-0 tw-flex tw-cursor-pointer tw-items-start tw-gap-2 tw-text-sm"
            >
              <Checkbox
                id={checkboxId}
                className="tw-mt-0.5 tw-shrink-0"
                checked={draft.switchViolation}
                disabled={!canEdit || saving}
                onChange={(e) =>
                  setDraft({ ...draft, switchViolation: e.target.checked })
                }
              />
              {t('chartImport.v73')}
            </label>
            <div className="tw-flex tw-justify-end tw-gap-2">
              <Button
                text={t('common.cancel')}
                variant="neutral"
                mode="outline"
                size="sm"
                disabled={saving}
                onClick={close}
              />
              <Button
                text={t('common.save')}
                variant="primary"
                size="sm"
                loading={saving}
                disabled={!canEdit}
                onClick={save}
              />
            </div>
          </div>
        </Popup>
      )}
    </>
  )
}

export default DayInfoButton
